import { useEffect } from 'react';

import { useLocation } from 'react-router';

import { useMediaQuery } from 'react-responsive';

import { useTour } from '@reactour/tour';

import { Sidebar } from './Sidebar';

import { BottomNavigation } from './BottomNavigation';

import { HelpAgentTrigger } from './HelpAgentTrigger';

import { HelpChatPanel } from './HelpChatPanel';

import { HelpChatProvider, useHelpChat } from './HelpChatProvider';

import { AiActivityProvider, useAiActivity } from './AiActivityProvider';

import { AiActivityDock } from './AiActivityDock';

import { ShellUserFirstNameProvider } from '../hooks/ShellUserFirstNameProvider';

import { useLywTourStorageKey } from './lywOnboardingTourSteps';

import '../styles/tailwind.css';



interface AppLayoutProps {

  children: React.ReactNode;

}



function TourStarter() {

  const { isOpen, setIsOpen, setCurrentStep } = useTour();

  const location = useLocation();

  const tourStorageKey = useLywTourStorageKey();



  useEffect(() => {

    if (isOpen) return;

    const path = location.pathname;

    const hash = typeof window !== 'undefined' ? window.location.hash.replace(/^#/, '') : '';

    // Include `/app` (dashboard index), not only `/app/...`

    const isLywRoute =

      path === '/app' ||

      path.startsWith('/app/') ||

      hash === '/app' ||

      hash.startsWith('/app/') ||

      hash.startsWith('app/');

    if (!isLywRoute) return;



    if (typeof window !== 'undefined') {

      sessionStorage.removeItem('lyw_start_tour_on_load');

    }

    if (window.localStorage.getItem(tourStorageKey)) {

      return;

    }



    const timer = setTimeout(() => {

      setCurrentStep(0);

      setIsOpen(true);

    }, 600);

    return () => clearTimeout(timer);

  }, [isOpen, location.pathname, location.hash, tourStorageKey, setIsOpen, setCurrentStep]);



  return null;

}



function AppLayoutContent({ children }: AppLayoutProps) {

  const isDesktop = useMediaQuery({ query: '(min-width: 640px)' });

  const { isOpen: helpOpen } = useHelpChat();

  const { isOpen: aiActivityOpen, sessionDebugMode } = useAiActivity();

  const showAiActivityDock = sessionDebugMode && aiActivityOpen;

  const hasSplitLayout = helpOpen || showAiActivityDock;



  return (

    <div className="lyw-container min-h-screen bg-gray-50">

      <TourStarter />

      <Sidebar />

      <main className="sm:ml-64 pb-16 sm:pb-0">

        <div

          className={

            hasSplitLayout

              ? 'flex h-[calc(100vh-4rem)] flex-col sm:h-screen sm:flex-row'

              : 'min-h-[calc(100vh-4rem)] sm:min-h-screen'

          }

        >

          <div
            data-help-content-root
            className={
              !hasSplitLayout
                ? 'min-h-full w-full'
                : helpOpen && !showAiActivityDock
                  ? 'h-1/2 min-h-0 overflow-auto sm:h-full sm:w-1/2'
                  : 'min-h-0 min-w-0 flex-1 overflow-auto'
            }
          >

            {children}

          </div>

          {helpOpen && (

            <div className="h-1/2 min-h-0 shrink-0 border-t border-gray-200 sm:h-full sm:w-1/2 sm:border-l sm:border-t-0">

              <HelpChatPanel />

            </div>

          )}

          {showAiActivityDock && <AiActivityDock />}

        </div>

      </main>

      {isDesktop ? (

        <>

          <HelpAgentTrigger />

          <BottomNavigation />

        </>

      ) : (

        <>

          <BottomNavigation />

          <HelpAgentTrigger />

        </>

      )}

    </div>

  );

}



export function AppLayout({ children }: AppLayoutProps) {

  return (

    <HelpChatProvider>

      <AiActivityProvider>

        <ShellUserFirstNameProvider>

          <AppLayoutContent>

            {children}

          </AppLayoutContent>

        </ShellUserFirstNameProvider>

      </AiActivityProvider>

    </HelpChatProvider>

  );

}
